interface StatusIndicatorProps {
  status: string;
}

/**
 * Status Priority (as defined by backend):
 * 1. Inactive - Machine is marked as inactive in the system (MachineStatusId = 2)
 * 2. Disconnected - Machine is active but not connected via OCPP
 * 3. Actual Status - Real-time connector status (Available, Charging, etc.)
 *
 * Inactive machines should be clearly distinguished from Disconnected machines:
 * - Inactive: Expected state, machine not operational (gray with power-off icon)
 * - Disconnected: Unexpected state, machine should be connected (gray with X icon)
 */

// SVG icon for available status (checkmark in circle)
const AvailableIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// SVG icon for disconnected status (circle with X or slash)
const DisconnectedIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// SVG icon for offline status (circle with horizontal line)
const OfflineIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// SVG icon for preparing status (circle with clock/timer indicator)
const PreparingIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 5V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// SVG icon for no contact status (circle with question mark)
const NoContactIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6.5 6C6.5 5.17 7.17 4.5 8 4.5C8.83 4.5 9.5 5.17 9.5 6C9.5 6.83 8.83 7.5 8 7.5V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
  </svg>
);

// SVG icon for inactive status (circle with power-off symbol)
const InactiveIcon = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 4V8M8 11V11.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
  </svg>
);

type StatusConfig = {
  icon: string | null;
  iconComponent?: React.FC;
  textColor: string;
  bgColor: string;
};

export function StatusIndicator({ status }: StatusIndicatorProps) {
  const getStatusConfig = (status: string): StatusConfig => {
    switch (status.toLowerCase()) {
      case 'charging':
        return {
          icon: '/assets/lightning-green.png',
          textColor: 'text-green-700',
          bgColor: 'bg-green-50'
        };
      case 'finishing':
        return {
          icon: '/assets/check.png',
          textColor: 'text-yellow-700',
          bgColor: 'bg-yellow-50'
        };
      case 'available':
        return {
          icon: null,
          iconComponent: AvailableIcon,
          textColor: 'text-blue-700',
          bgColor: 'bg-blue-50'
        };
      case 'preparing':
        return {
          icon: null,
          iconComponent: PreparingIcon,
          textColor: 'text-cyan-700',
          bgColor: 'bg-cyan-50'
        };
      case 'suspended':
      case 'suspendedev':
      case 'suspendedevse':
        return {
          icon: '/assets/lightning-yellow.png',
          textColor: 'text-yellow-700',
          bgColor: 'bg-yellow-50'
        };
      case 'offline':
        return {
          icon: null,
          iconComponent: OfflineIcon,
          textColor: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
      case 'disconnected':
        return {
          icon: null,
          iconComponent: DisconnectedIcon,
          textColor: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
      case 'neverconnected':
        return {
          icon: null,
          iconComponent: NoContactIcon,
          textColor: 'text-gray-500',
          bgColor: 'bg-gray-100'
        };
      case 'no contact':
        return {
          icon: null,
          iconComponent: NoContactIcon,
          textColor: 'text-gray-500',
          bgColor: 'bg-gray-100'
        };
      case 'inactive':
        return {
          icon: null,
          iconComponent: InactiveIcon,
          textColor: 'text-gray-700',
          bgColor: 'bg-gray-200'
        };
      case 'reserved':
        return {
          icon: '/assets/blue-dot.png',
          textColor: 'text-purple-700',
          bgColor: 'bg-purple-50'
        };
      case 'unavailable':
        return {
          icon: '/assets/red-warning.png',
          textColor: 'text-red-700',
          bgColor: 'bg-red-50'
        };
      case 'error':
        return {
          icon: '/assets/red-warning.png',
          textColor: 'text-red-700',
          bgColor: 'bg-red-50'
        };
      case 'faulted':
        return {
          icon: '/assets/red-warning.png',
          textColor: 'text-red-700',
          bgColor: 'bg-red-50'
        };
      default:
        return {
          icon: '/assets/blue-dot.png',
          textColor: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
    }
  };

  const config = getStatusConfig(status);

  // Format status text - handle special cases for proper casing
  const formatStatusText = (status: string): string => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'suspendedev') return 'SuspendedEv';
    if (lowerStatus === 'suspendedevse') return 'SuspendedEvse';
    if (lowerStatus === 'no contact') return 'No Contact';
    if (lowerStatus === 'neverconnected') return 'Never Connected';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const displayStatus = formatStatusText(status);
  const isNoContact = status.toLowerCase() === 'no contact';

  const IconComponent = config.iconComponent;

  return (
    <div
      className={`flex items-center justify-center gap-1.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}
      title={isNoContact ? 'No status available for this connector' : undefined}
    >
      {IconComponent ? (
        <IconComponent />
      ) : (
        <img src={config.icon!} alt="" className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      {displayStatus}
    </div>
  );
}
